import { Meeting, MeetingSession } from "@kalyzee/kast-web-sdk/websocket";
import React, { ForwardedRef, useState } from "react";

import { DateTimeDisplay } from "@kalyzee/escaet-ui";
import { MeetingSessionOvenMediaReceiverManagerBaseData, MeetingSourceElement } from "@kalyzee/kast-web-sdk";
import { Video } from "@kalyzee/kast-web-sdk/web-components";
import { OvenMediaReceiverWebRTCSession } from "@kalyzee/kast-web-sdk/webrtc";
import { PlayerItem } from "../../../../components/player/interfaces";
import { SecondaryScreenGrid } from "../../../../components/secondary-screens/grid/Grid";
import { getAudioContextForVolumeAnalyzer } from "../../../../helpers/audio";
import { useMeetingScreenSharingSource } from "../../../../hooks/meeting";
import { ScreenParams } from "../../../../hooks/screen";
import SamiiEscaetLogo from "../../../../assets/logo/samii-escaet.svg";
import styles from "./screenSharing.module.css";

export interface SecondaryScreenScreenSharingProps {
  meeting: Meeting;
  session: MeetingSession;
  videoSessions: OvenMediaReceiverWebRTCSession<MeetingSessionOvenMediaReceiverManagerBaseData<any>>[];
  screenParams: ScreenParams;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}

export interface SecondaryScreenScreenSharingRef {}

export const SecondaryScreenScreenSharing = React.forwardRef(
  (
    { meeting, session, videoSessions, screenParams, children, className, style }: SecondaryScreenScreenSharingProps,
    forwardRef: ForwardedRef<SecondaryScreenScreenSharingRef>
  ) => {
    const screenSharingSource = useMeetingScreenSharingSource();
    const [mediaStream, setMediaStream] = useState<MediaStream>();
    // ---------------------------------------------------------------------- //
    // ---------------------------  EFFECTS ----------------------------- //
    // ---------------------------------------------------------------------- //

    // ---------------------------------------------------------------------- //
    // ---------------------------  RENDER ----------------------------- //
    // ---------------------------------------------------------------------- //

    const renderContent = () => {
      const screenEnabled = session?.medias?.find((m) => m.video.enabled && m.extra?.type === "screen") ? true : false;
      if (videoSessions.length) {
        return (
          <SecondaryScreenGrid
            meeting={meeting}
            sessions={meeting.sessions ?? []}
            videoSessions={videoSessions}
            hideIfNoVideoSessions={true}
            grid={{
              column: undefined,
              row: undefined,
              noPages: true,
              autoAdjust: true,
            }}
            filter={(videoSession) => {
              return videoSessions.find((v) => v.getId() === videoSession.getId()) ? true : false;
            }}
            onItems={(items) => {
              if (screenEnabled && screenSharingSource && mediaStream) {
                return [new PlayerItem({ type: "stream", mediaStream: mediaStream }), ...items];
              }
              return items;
            }}
            style={{ width: "100%", height: "100%" }}
          />
        );
      }

      if (screenEnabled && screenSharingSource && mediaStream) {
        return <Video style={{ width: "100%", height: "100%" }} src={mediaStream} autoPlay muted />;
      }

      return (
        <div className={[styles.waiting, styles.background].join(" ")} style={{ fontSize: '1rem'}}>
          <div className={styles.header}>
            <img src={SamiiEscaetLogo} alt="logo" />
          </div>
          <div className={styles.content}>{"Aucun partage d'écran"}</div>
          <div className={styles.footer}>
            <DateTimeDisplay cx="textLeft" />
          </div>
        </div>
      );
    };

    const classes = [styles.container];
    if (className) classes.push(className);


    return (
      <div className={classes.join(" ")} style={style}>
        {renderContent()}
        {screenSharingSource && (
          <MeetingSourceElement
            style={{ display: "none" }}
            callbacks={{
              update(data) {
                setMediaStream(data.media);
              },
              destroy() {
                setMediaStream(undefined);
              },
            }}
            className={styles.screenSharing}
            source={screenSharingSource}
            using
            audioContext={getAudioContextForVolumeAnalyzer()}
          />
        )}
        {children}
      </div>
    );
  }
);
